// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { AdminNotification } from '../../types/admin';
import { format } from 'date-fns';
import { AdminPagination } from './AdminPagination';
import { AdminSearchBar } from './AdminSearchBar';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { AdminQuickFilters } from './AdminQuickFilters';
import { AdminLoadingSkeleton } from './AdminLoadingSkeleton';
import { AdminDateRangeFilter } from './AdminDateRangeFilter';
import { AdminColumnToggle } from './AdminColumnToggle';
import { useAdminToast } from './useAdminToast';
import { useDebounce } from './useDebounce';
import { exportToCSV, exportToJSON } from './AdminExportUtils';

interface AdminNotificationsProps {
  session: Session;
  profile: Profile | null;
}

export const AdminNotifications: React.FC<AdminNotificationsProps> = ({ session, profile }) => {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [allNotifications, setAllNotifications] = useState<AdminNotification[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [visibleColumns, setVisibleColumns] = useState<string[]>(['select', 'user', 'topic', 'title', 'email_sent', 'created_at']);
  const { showSuccess, showError } = useAdminToast();
  
  // Debounce search input
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    // Safety check: ensure allNotifications is an array
    if (!Array.isArray(allNotifications)) {
      setNotifications([]);
      return;
    }
    
    let filtered = allNotifications.filter((notif) => {
      // Filter out any invalid notification objects
      return notif && typeof notif === 'object' && notif.id;
    });
    
    // Apply quick filters
    if (activeQuickFilter === 'sent') {
      filtered = filtered.filter((notif) => notif.email_sent);
    } else if (activeQuickFilter === 'failed') {
      filtered = filtered.filter((notif) => !notif.email_sent);
    } else if (activeQuickFilter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter((notif) => {
        if (!notif.created_at) return false;
        try {
          const notifDate = new Date(notif.created_at);
          return !isNaN(notifDate.getTime()) && notifDate >= sevenDaysAgo;
        } catch (e) {
          return false;
        }
      });
    }
    
    // Apply date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((notif) => {
        if (!notif.created_at) return false;
        try {
          const notifDate = new Date(notif.created_at);
          return !isNaN(notifDate.getTime()) && notifDate >= dateRange.start! && notifDate <= dateRange.end!;
        } catch (e) {
          return false;
        }
      });
    }
    
    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter((notif) => {
        const userName = notif.profiles
          ? `${notif.profiles.first_name || ''} ${notif.profiles.last_name || ''}`.trim().toLowerCase()
          : '';
        const topicName = notif.law_topics?.name?.toLowerCase() || '';
        const title = notif.title?.toLowerCase() || '';
        const summary = notif.summary?.toLowerCase() || '';
        return userName.includes(query) || topicName.includes(query) || title.includes(query) || summary.includes(query);
      });
    }
    
    setNotifications(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [debouncedSearchQuery, allNotifications, activeQuickFilter, dateRange]);

  const quickFilters = [
    {
      id: 'all',
      label: 'All',
      icon: 'notifications',
      onClick: () => setActiveQuickFilter(undefined),
      count: Array.isArray(allNotifications) ? allNotifications.length : 0,
    },
    {
      id: 'sent',
      label: 'Sent',
      icon: 'mark_email_read',
      onClick: () => setActiveQuickFilter('sent'),
      count: Array.isArray(allNotifications) ? allNotifications.filter((notif) => notif && notif.email_sent).length : 0,
    },
    {
      id: 'failed',
      label: 'Failed',
      icon: 'error_outline',
      onClick: () => setActiveQuickFilter('failed'),
      count: Array.isArray(allNotifications) ? allNotifications.filter((notif) => notif && !notif.email_sent).length : 0,
    },
    {
      id: 'recent',
      label: 'Recent (7d)',
      icon: 'schedule',
      onClick: () => setActiveQuickFilter('recent'),
      count: Array.isArray(allNotifications) ? allNotifications.filter((notif) => {
        if (!notif || !notif.created_at) return false;
        try {
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          const notifDate = new Date(notif.created_at);
          return !isNaN(notifDate.getTime()) && notifDate >= sevenDaysAgo;
        } catch (e) {
          return false;
        }
      }).length : 0,
    },
  ];

  const handleExportCSV = () => {
    const dataToExport = selectedRows.length > 0 
      ? notifications.filter((notif) => selectedRows.includes(notif.id))
      : notifications;
    
    const csvData = dataToExport.map((notif) => ({
      User: notif.profiles
        ? `${notif.profiles.first_name || ''} ${notif.profiles.last_name || ''}`.trim() || 'Unknown'
        : 'Unknown',
      Topic: notif.law_topics?.name || 'Unknown',
      Title: notif.title,
      Summary: notif.summary,
      'Email Sent': notif.email_sent ? 'Yes' : 'No',
      'Sent At': notif.email_sent_at ? (() => {
        try {
          const date = new Date(notif.email_sent_at);
          if (isNaN(date.getTime())) return 'Invalid date';
          return format(date, 'yyyy-MM-dd HH:mm:ss');
        } catch (e) {
          return 'Invalid date';
        }
      })() : 'N/A',
      'Created At': notif.created_at ? format(new Date(notif.created_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    }));
    exportToCSV(csvData, selectedRows.length > 0 ? 'selected_notifications' : 'notifications');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected notification(s) exported to CSV successfully`
        : 'Notifications exported to CSV successfully'
    );
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRows.length > 0 
      ? notifications.filter((notif) => selectedRows.includes(notif.id))
      : notifications;
    
    exportToJSON(dataToExport, selectedRows.length > 0 ? 'selected_notifications' : 'notifications');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected notification(s) exported to JSON successfully`
        : 'Notifications exported to JSON successfully'
    );
  };

  const columnDefinitions = [
    {
      id: 'select',
      label: 'Select',
      defaultVisible: true,
    },
    {
      id: 'user',
      label: 'User',
      defaultVisible: true,
    },
    {
      id: 'topic',
      label: 'Topic',
      defaultVisible: true,
    },
    {
      id: 'title',
      label: 'Title',
      defaultVisible: true,
    },
    {
      id: 'email_sent',
      label: 'Email Sent',
      defaultVisible: true,
    },
    {
      id: 'created_at',
      label: 'Created',
      defaultVisible: true,
    },
  ];

  const handleToggleColumn = (columnId: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleToggleAllColumns = (visible: boolean) => {
    if (visible) {
      setVisibleColumns(columnDefinitions.map((col) => col.id));
    } else {
      setVisibleColumns(['select']); // Keep select column always visible
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    setShowDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', selectedRows);

      if (error) throw error;
      await loadNotifications();
      setSelectedRows([]);
      showSuccess(`${selectedRows.length} notification(s) deleted successfully`);
    } catch (err: any) {
      console.error('Error bulk deleting notifications:', err);
      showError(err.message || 'Failed to delete notifications');
    } finally {
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      // Load notifications with law_topics relationship
      // Note: user_id references auth.users, not profiles, so we need to fetch profiles separately
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select(
          `
          *,
          law_topics:topic_id (id, name)
        `,
          { count: 'exact' }
        )
        .order('created_at', { ascending: false });

      if (notificationsError) throw notificationsError;

      // Get unique user IDs
      const userIds = [...new Set((notificationsData || []).map(notif => notif.user_id))];
      
      // Fetch profiles for all users
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) {
        console.warn('Error loading profiles:', profilesError);
        // Continue without profiles - notifications will still load
      }

      // Create a map of user_id -> profile for quick lookup
      const profilesMap = new Map(
        (profilesData || []).map(profile => [profile.id, profile])
      );

      // Merge profiles data into notifications
      // Filter out any invalid notifications and ensure data integrity
      const notificationsWithProfiles = (notificationsData || [])
        .filter(notif => notif && notif.id && notif.user_id) // Ensure valid notification structure
        .map(notif => ({
          ...notif,
          profiles: profilesMap.get(notif.user_id) || null,
        }));

      setAllNotifications(notificationsWithProfiles);
      setTotalCount(notificationsWithProfiles.length);
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      showError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <div className="h-8 bg-luna-secondary/20 rounded animate-pulse w-64 mb-2" />
          <div className="h-4 bg-luna-secondary/10 rounded animate-pulse w-96" />
        </div>
        <AdminLoadingSkeleton rows={10} columns={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
              Notifications History
            </h1>
            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
              View all notifications sent to users ({notifications.length} of {totalCount})
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            {selectedRows.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 min-h-[44px] touch-manipulation"
              >
                Delete Selected ({selectedRows.length})
              </button>
            )}
            <AdminColumnToggle
              columns={columnDefinitions}
              visibleColumns={visibleColumns}
              onToggle={handleToggleColumn}
              onToggleAll={handleToggleAllColumns}
            />
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleExportCSV}
                className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 min-h-[44px] touch-manipulation"
                title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected notifications` : 'Export all notifications'}
              >
                <span className="sm:hidden">CSV</span>
                <span className="hidden sm:inline">Export CSV</span>
                {selectedRows.length > 0 && ` (${selectedRows.length})`}
              </button>
              <button
                onClick={handleExportJSON}
                className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px] touch-manipulation"
                title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected notifications` : 'Export all notifications'}
              >
                <span className="sm:hidden">JSON</span>
                <span className="hidden sm:inline">Export JSON</span>
                {selectedRows.length > 0 && ` (${selectedRows.length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <AdminQuickFilters filters={quickFilters} activeFilter={activeQuickFilter} className="mb-4" />

        {/* Date Range Filter */}
        <AdminDateRangeFilter
          onDateRangeChange={(start, end) => setDateRange({ start, end })}
          className="mb-4"
        />

        {/* Search Bar */}
        <AdminSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by user, topic, title, or summary..."
        />
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0 overscroll-x-contain touch-pan-x">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="w-full min-w-[640px] sm:min-w-0">
              <thead className="bg-luna-secondary/10">
                <tr>
                  {visibleColumns.includes('select') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark w-12 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedRows.length === notifications.length && notifications.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows(notifications.map((notif) => notif.id));
                          } else {
                            setSelectedRows([]);
                          }
                        }}
                        className="rounded border-luna-secondary/30"
                      />
                    </th>
                  )}
                  {visibleColumns.includes('user') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                      User
                    </th>
                  )}
                  {visibleColumns.includes('topic') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                      Topic
                    </th>
                  )}
                  {visibleColumns.includes('title') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                      Title
                    </th>
                  )}
                  {visibleColumns.includes('email_sent') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                      Email Sent
                    </th>
                  )}
                  {visibleColumns.includes('created_at') && (
                    <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                      Created
                    </th>
                  )}
                </tr>
              </thead>
            <tbody>
              {notifications.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
                        notifications
                      </span>
                      <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                        {searchQuery ? 'No notifications found matching your search' : 'No notifications found'}
                      </p>
                      <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {searchQuery
                          ? 'Try adjusting your search query'
                          : 'Notifications will appear here once they are sent to users'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                notifications
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((notif) => (
                <tr
                  key={notif.id}
                  className="border-t border-luna-secondary/10 hover:bg-luna-secondary/5"
                >
                  {visibleColumns.includes('select') && (
                    <td className="px-3 sm:px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(notif.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, notif.id]);
                          } else {
                            setSelectedRows(selectedRows.filter((id) => id !== notif.id));
                          }
                        }}
                        className="rounded border-luna-secondary/30"
                      />
                    </td>
                  )}
                  {visibleColumns.includes('user') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {notif.profiles
                        ? `${notif.profiles.first_name || ''} ${notif.profiles.last_name || ''}`.trim() ||
                          'Unknown'
                        : 'Unknown'}
                    </td>
                  )}
                  {visibleColumns.includes('topic') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {notif.law_topics?.name || 'Unknown'}
                    </td>
                  )}
                  {visibleColumns.includes('title') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      <div className="max-w-xs truncate" title={notif.title}>
                        {notif.title}
                      </div>
                    </td>
                  )}
                  {visibleColumns.includes('email_sent') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          notif.email_sent
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        {notif.email_sent ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.includes('created_at') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                      {notif.created_at ? (() => {
                        try {
                          const date = new Date(notif.created_at);
                          if (isNaN(date.getTime())) return 'Invalid date';
                          return format(date, 'MMM dd, yyyy HH:mm');
                        } catch (e) {
                          return 'Invalid date';
                        }
                      })() : 'N/A'}
                    </td>
                  )}
                </tr>
                  ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination */}
        {notifications.length > 0 && (
          <AdminPagination
            currentPage={currentPage}
            totalPages={Math.ceil(notifications.length / pageSize)}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalItems={notifications.length}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Notifications"
        message={`Are you sure you want to delete ${selectedRows.length} notification(s)? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowDeleteDialog(false)}
        variant="danger"
      />
    </div>
  );
};
